import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Sparkles, Rocket, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';

const Login = () => {
  const navigate = useNavigate();
  const { login, demoLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/pulse');
      } else {
        // Show the error message from the auth context
        setError(result.error || 'Invalid email or password');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login submit error:', err);
      setError(err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await demoLogin();
      
      if (result.success) {
        navigate('/pulse');
      } else {
        setError(result.error || 'Demo login failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('Demo login error:', err);
      setError('Demo login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      {/* Decorative Background Elements */}
      <div 
        className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-20"
        style={{ backgroundColor: DESIGN.colors.primary, transform: 'translate(-50%, -50%)' }}
      />
      <div 
        className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10"
        style={{ backgroundColor: DESIGN.colors.blue, transform: 'translate(50%, 50%)' }}
      />

      <div className="w-full max-w-md p-8 relative z-10">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                boxShadow: DESIGN.shadows.glowPrimary
              }}
            >
              <Sparkles className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 
            className="text-3xl font-bold tracking-tight mb-1"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            CognitiveOS
          </h1>
          <p className="text-sm font-medium tracking-wider uppercase" style={{ color: DESIGN.colors.textMuted }}>
            Cognitive Execution Operating System
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-2xl p-8"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            boxShadow: DESIGN.shadows.lg
          }}
        >
          <h2 
            className="text-xl font-semibold mb-6"
            style={{ color: DESIGN.colors.textPrimary }}
          >
            Sign in to your account
          </h2>

          {error && (
            <div 
              className="mb-5 p-4 rounded-xl flex items-start gap-3"
              style={{ backgroundColor: DESIGN.colors.errorSubtle }}
              data-testid="login-error"
            >
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: DESIGN.colors.error }} />
              <p className="text-sm font-medium" style={{ color: DESIGN.colors.error }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label 
                htmlFor="email"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Email address
              </Label>
              <div className="relative mt-2">
                <Mail 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" 
                  style={{ color: DESIGN.colors.textMuted }} 
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 rounded-xl pl-10"
                  placeholder="you@company.com"
                  style={{ 
                    borderColor: DESIGN.colors.border,
                    backgroundColor: DESIGN.colors.background
                  }}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label 
                htmlFor="password"
                className="text-sm font-medium"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                Password
              </Label>
              <div className="relative mt-2">
                <Lock 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" 
                  style={{ color: DESIGN.colors.textMuted }} 
                />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 rounded-xl pl-10 pr-10"
                  placeholder="Enter your password"
                  style={{ 
                    borderColor: DESIGN.colors.border,
                    backgroundColor: DESIGN.colors.background
                  }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors hover:bg-white/5"
                  style={{ color: DESIGN.colors.textMuted }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl font-semibold transition-all duration-200"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                color: 'white',
                boxShadow: '0 4px 12px rgba(0, 201, 167, 0.3)'
              }}
              data-testid="login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign in'}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: DESIGN.colors.border }} />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span 
                className="px-3" 
                style={{ backgroundColor: DESIGN.colors.card, color: DESIGN.colors.textMuted }}
              >
                Or continue with
              </span>
            </div>
          </div>

          {/* Demo Mode Button */}
          <Button
            type="button"
            onClick={handleDemoLogin}
            disabled={loading}
            variant="outline"
            className="w-full h-11 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
            style={{
              borderColor: DESIGN.colors.primary,
              color: DESIGN.colors.primary,
              backgroundColor: DESIGN.colors.primarySubtle
            }}
            data-testid="demo-login-button"
          >
            <Rocket className="w-4 h-4" />
            {loading ? 'Loading...' : 'Try Demo Mode'}
          </Button>
          <p className="text-xs text-center mt-2" style={{ color: DESIGN.colors.textMuted }}>
            Instant access with pre-loaded sample data
          </p>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="font-semibold transition-colors hover:opacity-80"
                style={{ color: DESIGN.colors.primary }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
